import {
  AppInsightsContext,
  Link,
  MetadataContext,
  ModuleBase,
  ResponsiveImage,
  TrackEventKey,
  TrackEventName,
  TrackEventType,
  UserContext,
} from '@/components';
import { ImageModel, LinkModel, ModuleBaseModel, ModuleSectionPropsModel } from '@/types';
import classNames from 'classnames';
import { useContext, useEffect, useState } from 'react';
import tailwindConfig from '../tailwind.screens';
import { useRouter } from 'next/router';

export type BannerTemplateModel = ModuleBaseModel<
  {
    banner: {
      bannerId: string;
      bannerImage: ImageModel;
      mobileImage: ImageModel;
      lable: string;
      bannerTitle: string; // HTML string
      titleTextColor: string;
      buttonBackgroundColor: string;
      buttonTextColor: string;
      descriptionTextColor: string;
      bannerDescription: string; // HTML string
      activePeriod: string; // ISO date string e.g. "2025-11-14T00:00:00Z"
      active: boolean;
      buttonText: string;
      buttonLink: LinkModel;
    };
  } & ModuleSectionPropsModel
>;

const BannerTemplate = (data: BannerTemplateModel) => {
  const {
    props: { banner, backgroundColour },
  } = data;

  const [thinImage, setThinImage] = useState<boolean>(false);
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);

  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);
  const { pageId } = useContext(MetadataContext);
  const router = useRouter();

  useEffect(() => {
    const _setIsDesktop = () => {
      if (window.innerWidth < parseInt(tailwindConfig.screens.md, 10)) {
        setImageLoaded(false);
        setThinImage(false);
      }
    };

    window.addEventListener('resize', _setIsDesktop);
    return () => window.removeEventListener('resize', _setIsDesktop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onImageLoaded = (pictire: HTMLPictureElement) => {
    setImageLoaded(true);

    if (pictire && pictire.querySelector('img')?.naturalHeight) {
      const naturalHeight = pictire.querySelector('img')?.naturalHeight || 0;

      if (window.innerWidth < parseInt(tailwindConfig.screens.md, 10)) {
        setThinImage(false);
      } else if (
        (window.innerWidth >= parseInt(tailwindConfig.screens.md, 10) &&
          window.innerWidth < parseInt(tailwindConfig.screens.lg, 10) &&
          naturalHeight < 130) ||
        (window.innerWidth >= parseInt(tailwindConfig.screens.lg, 10) &&
          window.innerWidth < parseInt(tailwindConfig.screens.xl, 10) &&
          naturalHeight < 150) ||
        (window.innerWidth >= parseInt(tailwindConfig.screens.xl, 10) &&
          window.innerWidth < parseInt(tailwindConfig.screens.xxl, 10) &&
          naturalHeight < 200) ||
        (window.innerWidth >= parseInt(tailwindConfig.screens.xxl, 10) && naturalHeight < 250)
      ) {
        setThinImage(true);
      }
    }
  };

  const showButton = banner.buttonText && banner.buttonLink && banner.buttonLink.href != '';

  const onBannerClick = () => {
    appInsights.trackCustomEvent(TrackEventName.PAGE, TrackEventKey.BANNER, TrackEventType.CLICK, user, {
      pageId,
      pagePath: router.asPath,
      bannerLabel: banner.lable,
      destination: banner.buttonLink?.href,
    });
  };

  if (!banner) {
    return null;
  }

  const mainComponent = (
    <>
      <div className={classNames('absolute h-full w-full', imageLoaded ? 'md:relative' : '')}>
        {banner.bannerImage && (
          <ResponsiveImage
            image={banner.bannerImage}
            widths={{ xs: 1, sm: 768, md: 1024, lg: 1440, xl: 1904 }}
            heights={{ xs: 1, sm: null }}
            className="hidden md:block"
            onLoad={(e) => onImageLoaded(e.currentTarget)}
          />
        )}
        {banner.mobileImage && (
          <ResponsiveImage
            image={banner.mobileImage}
            widths={{ xs: 550, sm: 1, md: 1, lg: 1, xl: 1 }}
            heights={{ xs: null, sm: 1, md: 1, lg: 1, xl: 1 }}
            className="md:hidden"
          />
        )}
      </div>
      <div
        className={classNames(
          'top-0 flex h-full w-full',
          (thinImage || !imageLoaded) && 'justify-center',
          imageLoaded ? (thinImage ? 'absolute py-9 md:py-0' : 'relative py-9 md:absolute') : 'relative py-9 lg:py-12',
        )}
      >
        <div
          className={classNames(
            'max-w-full flex-[0_0_100%] px-6',
            thinImage || !imageLoaded
              ? 'sm:px-3 md:px-3 lg:max-w-[900px] lg:px-2 xl:w-full xl:max-w-full xl:px-[150px]'
              : 'sm:max-w-[50%] sm:flex-[0_0_50%] sm:pr-0 sm:pl-[30px] lg:pl-20 xl:pl-[150px]',
          )}
        >
          <div
            className={classNames(
              'flex h-full flex-col items-start justify-center md:items-center',
              thinImage ? 'md:flex-row' : 'xxl:gap-9 gap-4 md:gap-3 lg:gap-4',
            )}
          >
            <div className={classNames(thinImage ? 'md:w-8/12' : 'w-full')}>
              <div className="xxl:gap-9 flex flex-col gap-4 md:gap-3 lg:gap-4">
                {banner.bannerTitle && (
                  <div
                    className="xxl:text-[40px] xxl:leading-[46px] text-[26px] leading-[30px] font-semibold md:text-base md:leading-5 lg:text-2xl lg:leading-7 xl:text-3xl xl:leading-9 [&_strong]:font-semibold"
                    style={{
                      color: banner.titleTextColor || '#ffffff',
                    }}
                    dangerouslySetInnerHTML={{ __html: banner.bannerTitle }}
                  />
                )}
                {banner.bannerDescription && (
                  <div
                    className={classNames(
                      thinImage || !imageLoaded ? 'w-full md:w-11/12 xl:w-10/12' : 'w-full',
                      'xxl:text-2xl xxl:leading-7 text-sm leading-[18px] font-light md:text-xs md:leading-3.5 lg:text-sm lg:leading-4 xl:text-xl xl:leading-6',
                    )}
                    style={{
                      color: banner.descriptionTextColor || '#ffffff',
                    }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: banner.bannerDescription }} />
                  </div>
                )}
              </div>
            </div>
            {showButton && (
              <div className={classNames(thinImage ? 'text-right md:w-4/12' : 'w-full')}>
                <Link
                  link={banner.buttonLink}
                  style={{
                    color: banner.buttonTextColor || '#ffffff',
                    backgroundColor: banner.buttonBackgroundColor || 'transparent',
                  }}
                  className={classNames(
                    'inline-flex h-[52px] items-center justify-center rounded-full px-6 text-center whitespace-normal sm:h-[28px] md:whitespace-nowrap lg:h-[44px] xl:h-[52px]',
                    'xxl:text-[26px] text-lg leading-[normal] md:text-sm lg:text-base xl:text-xl',
                  )}
                  onClick={onBannerClick}
                >
                  {banner.buttonText}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <ModuleBase
      data={{ ...data, props: { ...data.props, backgroundColour: '' } }}
      noPadding
      className="flex justify-center overflow-hidden print:hidden"
    >
      <div
        className="relative flex w-full max-w-[2100px]"
        style={{
          backgroundColor: backgroundColour ? backgroundColour : 'inherit',
        }}
      >
        {showButton ? (
          <div className={classNames('h-full w-full', { 'cursor-pointer': !showButton })}>{mainComponent}</div>
        ) : (
          <Link link={banner.buttonLink} className={classNames('w-full cursor-pointer')} onClick={onBannerClick}>
            {mainComponent}
          </Link>
        )}
      </div>
    </ModuleBase>
  );
};

export default BannerTemplate;
